import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  BoxProps,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { QueryError } from "../../../types";
import { useSnackbar } from "../../../hooks/useSnackbar";
import DisplayMultipleFilesItem from "./DisplayMultipleFilesItem";

type Props = {
  displayName: string;
  value?: { name: string; downloadId: string }[] | null;
  download: (downloadId: string) => Promise<void>;
  emptyPlaceHolder?: string;
  fontSize?: string;
  color?: string;
  disabled?: boolean;
  horizontal?: boolean;
  horizontalLabelWidthPercentage?: number;
  boxProps?: BoxProps;
};

const DisplayMultipleFiles = ({
  displayName,
  value,
  download,
  emptyPlaceHolder,
  fontSize,
  color,
  disabled,
  horizontal,
  horizontalLabelWidthPercentage,
  boxProps,
}: Props) => {
  const theme = useTheme();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { showErrorSnackbar } = useSnackbar();

  const handleDownload = async (id: string) => {
    try {
      setDownloadLoading(true);
      await download(id);
    } catch (error) {
      showErrorSnackbar(
        "Erreur dans le téléchargement.",
        "error",
        error instanceof QueryError ? error : undefined
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Box
      color={disabled ? theme.palette.text.disabled : color ? color : undefined}
      display={horizontal ? "flex" : undefined}
      alignItems={horizontal ? "center" : undefined}
      gap={horizontal ? "8px" : undefined}
      width={horizontal ? "100%" : undefined}
      {...boxProps}
    >
      <Typography
        fontWeight={"bold"}
        fontSize={fontSize && fontSize}
        color="inherit"
        width={
          horizontal && horizontalLabelWidthPercentage
            ? `${horizontalLabelWidthPercentage}%`
            : undefined
        }
        flexShrink="0"
        lineHeight={1.4}
      >
        {displayName ? displayName : "\u00A0"}
      </Typography>
      {value && value.length > 0 ? (
        value.map((val, idx) => (
          <DisplayMultipleFilesItem
            fontSize={fontSize}
            value={val}
            download={download}
            key={val.downloadId}
          />
        ))
      ) : (
        <Typography
          fontSize={fontSize && fontSize}
          color="inherit"
          lineHeight={1.3}
          whiteSpace="pre-line"
          sx={{ wordBreak: "break-word" }}
        >
          {emptyPlaceHolder ? emptyPlaceHolder : "\u00A0"}
        </Typography>
      )}
    </Box>
  );
};

export default DisplayMultipleFiles;
